import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { type NavGroup, type NavItem, type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import {
    BarChart3,
    BookOpenText,
    Bug,
    Building2,
    Calculator,
    CalendarDays,
    Car,
    Contact,
    EyeOff,
    FileCheck2,
    FolderKanban,
    GanttChartSquare,
    Home,
    Magnet,
    MonitorSmartphone,
    ScrollText,
    ShoppingCart,
    Ticket,
    Users,
} from 'lucide-react';
import { useMemo } from 'react';

const year = new Date().getFullYear();

/**
 * Canonical navigation, split into logical sections. Shared by the desktop
 * sidebar and the mobile drawer so there is a single source of truth.
 * `translationKey` and `visibilityKey` are data-driven (no per-item ternaries).
 */
export const NAV_SECTIONS: NavGroup[] = [
    {
        title: 'Overview',
        titleKey: 'navigation.section_overview',
        items: [
            { title: 'Home', url: '/homepage', icon: Home, translationKey: 'navigation.home', visibilityKey: 'home' },
            {
                title: 'Analytics',
                url: '/analytics',
                icon: BarChart3,
                requiredPermission: 'view-analysis',
                translationKey: 'navigation.analytics',
                visibilityKey: 'analytics',
            },
        ],
    },
    {
        title: 'Sales',
        titleKey: 'navigation.section_sales',
        items: [
            {
                title: 'Companies',
                url: '/companies',
                icon: Building2,
                requiredPermission: 'companies.read',
                translationKey: 'navigation.companies',
                visibilityKey: 'companies',
            },
            {
                title: 'Contacts',
                url: '/people',
                icon: Contact,
                requiredPermission: 'contacts.read',
                translationKey: 'navigation.contacts',
                visibilityKey: 'contacts',
            },
            {
                title: 'Leads',
                url: '/leads',
                icon: Magnet,
                requiredPermission: 'leads.read',
                translationKey: 'navigation.leads',
                visibilityKey: 'leads',
            },
            {
                title: 'Calculation Requests',
                url: '/calculation-requests',
                icon: Calculator,
                requiredPermission: 'calculation-requests.read',
                translationKey: 'navigation.calculation_requests',
                visibilityKey: 'calculationRequests',
            },
        ],
    },
    {
        title: 'Operations',
        titleKey: 'navigation.section_operations',
        items: [
            {
                title: 'Projects',
                url: '/projects',
                icon: FolderKanban,
                requiredPermission: 'projects.read',
                translationKey: 'navigation.projects',
                visibilityKey: 'projects',
                children: [
                    {
                        title: 'Local',
                        url: `/projects?timeline_year=${year}&country_type=local`,
                        requiredPermission: 'projects.read',
                        translationKey: 'navigation.projects_local',
                    },
                    {
                        title: 'Export',
                        url: `/projects?timeline_year=${year}&country_type=export`,
                        requiredPermission: 'projects.read',
                        translationKey: 'navigation.projects_export',
                    },
                ],
            },
            {
                title: 'Service work orders',
                url: '/work-orders',
                icon: Car,
                requiredPermission: 'work-orders.read',
                translationKey: 'navigation.work_orders',
                visibilityKey: 'workOrders',
            },
            {
                title: 'Purchase Orders',
                url: '/purchase-orders',
                icon: ShoppingCart,
                requiredPermission: 'purchase-orders.read',
                translationKey: 'navigation.purchase_orders',
                visibilityKey: 'purchaseOrders',
            },
            {
                title: 'Service Requests',
                url: '/service-requests/catalog',
                icon: Ticket,
                translationKey: 'navigation.service_requests',
                visibilityKey: 'serviceRequests',
                children: [
                    { title: 'My Requests', url: '/service-requests', translationKey: 'navigation.service_requests_mine' },
                    {
                        title: 'Service Queue',
                        url: '/admin/service-requests',
                        requiredPermission: 'service-requests.see-all',
                        translationKey: 'navigation.service_requests_queue',
                    },
                    {
                        title: 'Request Templates',
                        url: '/admin/service-request-templates',
                        requiredPermission: 'service-requests.manage-templates',
                        translationKey: 'navigation.service_requests_templates',
                    },
                ],
            },
        ],
    },
    {
        title: 'Tools',
        titleKey: 'navigation.section_tools',
        items: [
            {
                title: 'MS Project viewer',
                url: '/mpp',
                icon: GanttChartSquare,
                requiredPermission: 'mpp.read',
                translationKey: 'navigation.mpp',
            },
            {
                title: 'ADOC documents',
                url: '/adoc',
                icon: FileCheck2,
                requiredPermission: 'adoc.read',
                translationKey: 'navigation.adoc',
            },
        ],
    },
    {
        title: 'People',
        titleKey: 'navigation.section_people',
        items: [
            {
                title: 'Colleagues',
                url: '/employees',
                icon: Users,
                requiredPermission: 'employees.read',
                translationKey: 'navigation.colleagues',
                visibilityKey: 'colleagues',
            },
            {
                title: 'Vacations',
                url: '/vacations',
                icon: CalendarDays,
                translationKey: 'navigation.vacations',
                visibilityKey: 'vacations',
            },
        ],
    },
    {
        title: 'Workspace',
        titleKey: 'navigation.section_workspace',
        items: [
            {
                title: 'Knowledge base',
                url: '/knowledge-base',
                icon: BookOpenText,
                translationKey: 'navigation.knowledge_base',
                visibilityKey: 'knowledgeBase',
            },
            {
                title: 'Bug reports',
                url: '/admin/bug-reports',
                icon: Bug,
                requiredPermission: 'bug-reports.read',
                translationKey: 'navigation.bug_reports',
            },
            {
                title: 'Remote Devices',
                url: '/remote-devices',
                icon: MonitorSmartphone,
                requiredPermission: 'remote-devices.view',
                translationKey: 'navigation.remote_devices',
            },
            {
                title: 'Audit Log',
                url: '/admin/activity-log',
                icon: ScrollText,
                requiredPermission: 'activity-log.view',
                translationKey: 'navigation.audit_log',
            },
        ],
    },
];

/**
 * Build the sectioned nav groups plus an optional collapsible "Hidden" group
 * (items an administrator has toggled off in settings but still needs to reach).
 * Permission filtering happens in NavMain against `auth.abilities`.
 */
export function useNavGroups(): { sections: NavGroup[]; hiddenGroup: NavGroup | null } {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const page = usePage<SharedData>();

    const authWithRoles = page.props?.auth as SharedData['auth'] & { roles?: string[] };
    const isAdministrator = authWithRoles?.user?.role === 'Administrator' || (authWithRoles?.roles ?? []).includes('Administrator');
    const navigationVisibility = settings.navigationVisibility as Record<string, boolean | undefined> | undefined;

    return useMemo(() => {
        const isVisible = (item: NavItem) => !item.visibilityKey || (navigationVisibility?.[item.visibilityKey] ?? true) === true;

        const translateItem = (item: NavItem): NavItem => ({
            ...item,
            title: item.translationKey ? t(item.translationKey, item.title) : item.title,
            children: item.children?.map(translateItem),
        });

        const sections: NavGroup[] = NAV_SECTIONS.map((section) => ({
            ...section,
            title: section.titleKey ? t(section.titleKey, section.title) : section.title,
            items: section.items.filter(isVisible).map(translateItem),
        })).filter((section) => section.items.length > 0);

        const hiddenItems = isAdministrator
            ? NAV_SECTIONS.flatMap((section) => section.items)
                  .filter((item) => item.visibilityKey && (navigationVisibility?.[item.visibilityKey] ?? true) === false)
                  .map(translateItem)
            : [];

        const hiddenGroup: NavGroup | null =
            hiddenItems.length > 0
                ? {
                      title: t('navigation.hidden', 'Hidden'),
                      items: hiddenItems,
                      collapsible: true,
                      defaultOpen: false,
                      icon: EyeOff,
                  }
                : null;

        return { sections, hiddenGroup };
    }, [t, navigationVisibility, isAdministrator]);
}
